import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
import { 
  MessageSquare, Send, Plus, User, Clock, CheckCheck, 
  Tag, FileText, Building2, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

/**
 * VENDOR CHAT INTERFACE
 * 
 * Chat that works independent of project association.
 * Used for:
 * - Rate negotiations
 * - Category requests
 * - General vendor-admin communication
 */
export default function VendorChat() {
  const [searchParams] = useSearchParams();
  const chatIdFromUrl = searchParams.get('chat');
  
  const [loading, setLoading] = useState(true);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [newChatSubject, setNewChatSubject] = useState('');
  
  const messagesEndRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isVendor = user.role === 'vendor';
  const isAdmin = ['owner', 'staff', 'admin'].includes(user.role);

  useEffect(() => {
    fetchChats();
  }, []);

  useEffect(() => {
    if (chatIdFromUrl && chats.length > 0) {
      const chat = chats.find(c => c.id === chatIdFromUrl);
      if (chat) {
        handleSelectChat(chat);
      }
    }
  }, [chatIdFromUrl, chats]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChats = async () => {
    try {
      const response = await axios.get(`${API}/vendor/chats`);
      setChats(response.data || []);
    } catch (error) {
      console.error('Failed to fetch chats:', error);
      toast.error('Failed to load chats');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChat = async (chat) => {
    setSelectedChat(chat);
    try {
      const response = await axios.get(`${API}/vendor/chats/${chat.id}/messages`);
      setMessages(response.data.messages || []);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/vendor/chats/${selectedChat.id}/messages`, {
        message: newMessage.trim()
      });
      setMessages(prev => [...prev, response.data.message]);
      setNewMessage('');
      
      // Update chat in list to show latest message
      setChats(prev => prev.map(c => 
        c.id === selectedChat.id 
          ? { ...c, updated_at: new Date().toISOString() }
          : c
      ));
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleStartNewChat = async () => {
    if (!newChatSubject.trim()) {
      toast.error('Please enter a subject');
      return;
    }

    try {
      const response = await axios.post(`${API}/vendor/chats/start`, {
        subject: newChatSubject.trim()
      });
      
      toast.success('Chat started successfully');
      setNewChatOpen(false);
      setNewChatSubject('');
      
      // Add to chat list and select it
      const newChat = response.data.chat;
      setChats(prev => [newChat, ...prev]);
      handleSelectChat(newChat);
    } catch (error) {
      toast.error('Failed to start chat');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(date);
  };

  const getChatIcon = (type) => {
    switch (type) {
      case 'rate_submission': return <FileText size={16} className="text-green-600" />;
      case 'category_request': return <Tag size={16} className="text-purple-600" />;
      default: return <MessageSquare size={16} className="text-blue-600" />;
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="vendor-chat-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-playfair text-2xl md:text-3xl font-bold text-stone-900 mb-1">
              {isVendor ? 'Chat with Admin' : 'Vendor Chats'}
            </h1>
            <p className="text-stone-500 text-sm">
              {isVendor 
                ? 'Discuss rates, categories, and queries with the admin team'
                : 'Manage conversations with vendors'
              }
            </p>
          </div>
          <Button
            onClick={() => setNewChatOpen(true)}
            className="btn-primary"
            data-testid="start-new-chat-btn"
          >
            <Plus size={18} className="mr-2" />
            New Chat
          </Button>
        </div>

        <div className="grid grid-cols-12 gap-6 h-[calc(100vh-220px)] min-h-[500px]">
          {/* Chat List Sidebar */}
          <div className="col-span-4 bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="p-4 border-b border-stone-200">
              <h3 className="font-semibold text-stone-900">Conversations</h3>
              <p className="text-xs text-stone-500">{chats.length} chat{chats.length !== 1 ? 's' : ''}</p>
            </div>
            
            <ScrollArea className="h-[calc(100%-60px)]">
              {loading ? (
                <div className="p-4 text-center text-stone-500">Loading...</div>
              ) : chats.length === 0 ? (
                <div className="p-6 text-center">
                  <MessageSquare className="mx-auto h-10 w-10 text-stone-300 mb-3" />
                  <p className="text-stone-500">No conversations yet</p>
                  <p className="text-xs text-stone-400 mt-1">Start a new chat to begin</p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {chats.map((chat) => (
                    <button
                      key={chat.id}
                      onClick={() => handleSelectChat(chat)}
                      className={`w-full p-4 text-left hover:bg-stone-50 transition-colors ${
                        selectedChat?.id === chat.id ? 'bg-stone-100' : ''
                      }`}
                      data-testid={`chat-${chat.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          {getChatIcon(chat.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-stone-900 truncate">
                              {isAdmin ? chat.vendor_name : 'Admin'}
                            </p>
                            {chat.unread_count > 0 && (
                              <Badge variant="destructive" className="ml-2 px-1.5 py-0 text-xs">
                                {chat.unread_count}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-stone-600 truncate">{chat.subject}</p>
                          <p className="text-xs text-stone-400 mt-1">{formatTime(chat.updated_at)}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Chat Messages Area */}
          <div className="col-span-8 bg-white rounded-xl border border-stone-200 flex flex-col overflow-hidden">
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-stone-200 bg-stone-50">
                  <div className="flex items-center gap-3">
                    {getChatIcon(selectedChat.type)}
                    <div>
                      <h3 className="font-semibold text-stone-900">
                        {isAdmin ? selectedChat.vendor_name : 'Admin Team'}
                      </h3>
                      <p className="text-sm text-stone-500">{selectedChat.subject}</p>
                    </div>
                    {selectedChat.type === 'rate_submission' && (
                      <Badge className="ml-auto bg-green-100 text-green-700 border-green-200">
                        Rate Discussion
                      </Badge>
                    )}
                    {selectedChat.type === 'category_request' && (
                      <Badge className="ml-auto bg-purple-100 text-purple-700 border-purple-200">
                        Category Request
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.length === 0 ? (
                      <div className="text-center py-8 text-stone-500">
                        <MessageSquare className="mx-auto h-8 w-8 text-stone-300 mb-2" />
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      messages.map((msg) => {
                        const isOwnMessage = msg.sender_id === user.id;
                        const isSystemMessage = msg.type === 'system';
                        
                        if (isSystemMessage) {
                          return (
                            <div key={msg.id} className="flex justify-center">
                              <div className="bg-stone-100 text-stone-600 text-xs px-3 py-1.5 rounded-full">
                                {msg.message}
                              </div>
                            </div>
                          );
                        }
                        
                        return (
                          <div
                            key={msg.id}
                            className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[70%] rounded-lg px-4 py-2.5 ${
                                isOwnMessage
                                  ? 'bg-brand-primary text-white rounded-br-none'
                                  : 'bg-stone-100 text-stone-900 rounded-bl-none'
                              }`}
                            >
                              {!isOwnMessage && (
                                <p className="text-xs font-medium mb-1 opacity-70">
                                  {msg.sender_name}
                                </p>
                              )}
                              <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                              <div className={`flex items-center gap-1 mt-1 text-xs ${
                                isOwnMessage ? 'text-white/70 justify-end' : 'text-stone-400'
                              }`}>
                                <Clock size={10} />
                                <span>{formatTime(msg.created_at)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-stone-200">
                  <div className="flex gap-3">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1"
                      data-testid="chat-message-input"
                    />
                    <Button
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="btn-primary"
                      data-testid="send-message-btn"
                    >
                      <Send size={18} className={sending ? 'animate-pulse' : ''} />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="mx-auto h-12 w-12 text-stone-300 mb-3" />
                  <h3 className="font-medium text-stone-900 mb-1">Select a conversation</h3>
                  <p className="text-sm text-stone-500">
                    Choose a chat from the list or start a new one
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* New Chat Dialog */}
        <Dialog open={newChatOpen} onOpenChange={setNewChatOpen}>
          <DialogContent className="max-w-md" data-testid="new-chat-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="text-blue-600" size={20} />
                Start New Chat
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-stone-700">Subject</label>
                <Input
                  value={newChatSubject}
                  onChange={(e) => setNewChatSubject(e.target.value)}
                  placeholder="e.g., Rate query for Electrical category"
                  className="mt-1"
                  data-testid="new-chat-subject"
                />
              </div>
              <p className="text-xs text-stone-500">
                Start a conversation with the {isVendor ? 'admin team' : 'vendor'} about rates, 
                categories, or any general queries.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setNewChatOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleStartNewChat}
                className="btn-primary"
                data-testid="confirm-start-chat"
              >
                Start Chat
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
